import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store/store";
import OTPInput from "react-otp-input";
import Modal from "./Modal";
import axios from "axios";
import config from "../util/config";
import ButtonLoader from "./ButtonLoader/ButtonLoader";

interface OTPVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (userData: any) => void;
  initialMobileNumber?: string;
  isUserLoggedIn?: boolean;
}

const OTPVerificationModal: React.FC<OTPVerificationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialMobileNumber = "",
  isUserLoggedIn = false,
}) => {
  const [step, setStep] = useState<"mobile" | "otp" | "email">("mobile");
  const [mobileNumber, setMobileNumber] = useState(initialMobileNumber);
  const [otpValue, setOtpValue] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(60);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [userExists, setUserExists] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [emailRequired, setEmailRequired] = useState(false);
  const [accessToken, setAccessToken] = useState<string>("");

  const windowWidth = window.innerWidth;

  const authState = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (initialMobileNumber) {
      setMobileNumber(initialMobileNumber);
      if (isUserLoggedIn) {
        setStep("mobile");
      }
    }
  }, [initialMobileNumber, isUserLoggedIn]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (timeRemaining > 0 && step === "otp") {
      timer = setInterval(() => {
        setTimeRemaining((prev) => prev - 1);
      }, 1000);
    } else if (timeRemaining === 0) {
      setResendDisabled(false);
    }

    return () => clearInterval(timer);
  }, [timeRemaining, step]);

  const checkMobileNumber = async (mobile: string) => {
    try {
      setLoading(true);
      const response = await axios.get(
        config.apiUrl + `/api/auth/user/details/${mobile}`,
        { headers: { accept: "*/*" } }
      );

      if (response.status === 200 && response?.data?.data) {
        setUserExists(true);
        setUserData(response.data.data);

        if (!response.data.data.email || response.data.data.email === "") {
          setEmailRequired(true);
        }
      } else {
        setUserExists(false);
      }
      setLoading(false);
      return true;
    } catch (error) {
      setUserExists(false);
      setLoading(false);
      return false;
    }
  };

  const handleSendOTP = async () => {
    if (mobileNumber.length !== 10 || !/^\d{10}$/.test(mobileNumber)) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const response = await axios.get(
        config.apiUrl + `/api/auth/mobile-otp-generation?mobileNumber=${mobileNumber}`,
        { headers: { accept: "*/*" } }
      );

      if (response.status >= 200 && response.status < 300) {
        await checkMobileNumber(mobileNumber);

        setStep("otp");
        setTimeRemaining(60);
        setResendDisabled(true);
      } else {
        setError("Failed to send OTP. Please try again.");
      }
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otpValue.length !== 6) {
      setError("Please enter the complete 6-digit OTP");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const response = await axios.post(
        config.apiUrl + '/api/auth/verify-otp',
        {
          mobileNumber: mobileNumber,
          otp: otpValue
        },
        {
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status >= 200 && response.status < 300) {
        const token = response.data?.accessToken;
        if (token) {
          setAccessToken(token);
          localStorage.setItem("accessToken", token);

          if (response.data?.refreshToken) {
            localStorage.setItem("refreshToken", response.data.refreshToken);
          }
        }

        if (response.data?.user) {
          setUserData(response.data.user);
        } else {
          await checkMobileNumber(mobileNumber);
        }

        if (emailRequired || (response.data?.user && (!response.data.user.email || response.data.user.email === ""))) {
          setStep("email");
        } else {
          localStorage.setItem("verifiedPhoneNumber", mobileNumber);

          onSuccess(userData || response.data?.user || { mobileNumber });
          resetForm();
          onClose();
        }
      } else {
        setError("Invalid OTP. Please try again.");
      }
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to verify OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const response = await axios.post(
        config.apiUrl + '/api/auth/signup',
        {
          firstName: userData?.firstName || "",
          lastName: userData?.lastName || "",
          email: email,
          mobileNumber: mobileNumber
        },
        {
          headers: {
            'Authorization': accessToken,
            'Content-Type': 'application/json',
            'accept': 'application/json'
          }
        }
      );

      if (response.status >= 200 && response.status < 300) {
        const updatedUserData = { ...userData, email };

        localStorage.setItem("verifiedPhoneNumber", mobileNumber);

        onSuccess(updatedUserData);
        resetForm();
        onClose();
      } else {
        setError("Failed to update email. Please try again.");
      }
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to update email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep("mobile");
    setMobileNumber(initialMobileNumber || "");
    setOtpValue("");
    setEmail("");
    setError(null);
    setTimeRemaining(60);
    setResendDisabled(false);
    setUserExists(false);
    setUserData(null);
    setEmailRequired(false);
    setAccessToken("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-white p-6 rounded-lg shadow-lg w-[90vw] max-w-md">
        <h2 className="text-xl font-bold mb-4 text-center">
          {step === "mobile" && "Phone Verification"}
          {step === "otp" && "Enter OTP"}
          {step === "email" && "Email Verification"}
        </h2>

        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

        {step === "mobile" && (
          <div className="space-y-4">
            <div>
              <label htmlFor="mobileNumber" className="block text-sm font-medium text-gray-700">
                Mobile Number
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <span className="text-gray-500">+91</span>
                </div>
                <input
                  type="text"
                  id="mobileNumber"
                  value={mobileNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 10) {
                      setMobileNumber(value);
                    }
                  }}
                  disabled={isUserLoggedIn}
                  className={`block w-full pl-12 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                    isUserLoggedIn ? "bg-gray-100" : ""
                  }`}
                  placeholder="Enter 10 digit mobile number"
                  maxLength={10}
                />
              </div>
            </div>
            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleSendOTP}
                disabled={mobileNumber.length !== 10 || loading || isUserLoggedIn}
                className={`inline-flex items-center justify-center w-32 h-10 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                  ${
                    mobileNumber.length !== 10 || loading || isUserLoggedIn
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-blue hover:bg-indigo-700"
                  }`}
              >
                {loading ? <ButtonLoader /> : "Send OTP"}
              </button>
            </div>
            {isUserLoggedIn && (
              <div className="text-sm text-center text-green-600">
                You are already logged in. Click the button below to continue.
              </div>
            )}
            {isUserLoggedIn && (
              <div className="flex justify-center mt-4">
                <button
                  type="button"
                  onClick={() => onSuccess(userData || { mobileNumber })}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue hover:bg-indigo-700"
                >
                  Continue
                </button>
              </div>
            )}
          </div>
        )}

        {step === "otp" && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 text-center">
              Enter the 6-digit code sent to <span className="font-semibold">+91 {mobileNumber}</span>
            </p>
            <div className="flex justify-center my-6">
              <OTPInput
                value={otpValue}
                onChange={setOtpValue}
                numInputs={6}
                renderSeparator={<span className="w-2"></span>}
                renderInput={(props) => (
                  <input
                    {...props}
                    className="w-10 h-10 text-center border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                )}
                inputStyle={{
                  width: windowWidth > 700 ? "3rem" : "2.5rem",
                  height: windowWidth > 700 ? "3rem" : "2.5rem",
                  fontSize: "1.2rem",
                  fontWeight: "600",
                }}
              />
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
              <button
                type="button"
                disabled={resendDisabled}
                onClick={handleSendOTP}
                className="text-sm text-blue hover:text-indigo-700"
              >
                {resendDisabled
                  ? `Resend OTP in ${timeRemaining}s`
                  : "Resend OTP"}
              </button>
              <button
                type="button"
                onClick={handleVerifyOTP}
                disabled={otpValue.length !== 6 || loading}
                className={`inline-flex items-center justify-center w-32 h-10 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                  ${
                    otpValue.length !== 6 || loading
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-blue hover:bg-indigo-700"
                  }`}
              >
                {loading ? <ButtonLoader /> : "Verify OTP"}
              </button>
            </div>
            <div className="text-xs text-gray-500 text-center mt-4">
              Changed your mind? <button onClick={() => setStep("mobile")} className="text-blue hover:underline">Change mobile number</button>
            </div>
          </div>
        )}

        {step === "email" && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 text-center">
              Please provide your email address to complete verification
            </p>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Enter your email address"
              />
            </div>
            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleEmailSubmit}
                disabled={!email || loading}
                className={`inline-flex items-center justify-center w-32 h-10 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                  ${
                    !email || loading
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-blue hover:bg-indigo-700"
                  }`}
              >
                {loading ? <ButtonLoader /> : "Submit"}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default OTPVerificationModal;